import type { PlatformAiSettings } from '@/api/schemas/ai';

/**
 * The types of the deployment settings form.
 *
 * They live apart from the component so that the field groups and the form
 * shell can both speak about them without importing each other.
 */
export type DeploymentFormState = {
  aiEnabled: boolean;
  defaultProviderId: string;
  defaultModelId: string;
  defaultPromptVersion: string;
  maxOutputTokens: string;
  temperature: string;
  platformMonthlyRequestLimit: string;
  platformMonthlyTokenLimit: string;
  tenantDefaultRequestLimit: string;
  tenantDefaultTokenLimit: string;
  tenantOwnedProviderAllowed: boolean;
  platformFallbackAllowed: boolean;
};

/** Writes one field of the deployment form state. */
export type DeploymentFormChange = <K extends keyof DeploymentFormState>(
  key: K,
  value: DeploymentFormState[K],
) => void;

/** The values the form collects, ready to be PATCHed. */
export type UpdatePlatformSettingsValues = {
  aiEnabled: boolean;
  defaultProviderId: string | null;
  defaultModelId: string | null;
  defaultPromptVersion: string | null;
  maxOutputTokens: number | null;
  temperature: number | null;
  platformMonthlyRequestLimit: number | null;
  platformMonthlyTokenLimit: number | null;
  tenantDefaultRequestLimit: number | null;
  tenantDefaultTokenLimit: number | null;
  tenantOwnedProviderAllowed: boolean;
  platformFallbackAllowed: boolean;
};

export function toDeploymentFormState(settings: PlatformAiSettings): DeploymentFormState {
  return {
    aiEnabled: settings.aiEnabled,
    defaultProviderId: settings.defaultProviderId ?? '',
    defaultModelId: settings.defaultModelId ?? '',
    defaultPromptVersion: settings.defaultPromptVersion ?? '',
    maxOutputTokens: settings.maxOutputTokens === null ? '' : String(settings.maxOutputTokens),
    temperature: settings.temperature === null ? '' : String(settings.temperature),
    platformMonthlyRequestLimit:
      settings.platformMonthlyRequestLimit === null
        ? ''
        : String(settings.platformMonthlyRequestLimit),
    platformMonthlyTokenLimit:
      settings.platformMonthlyTokenLimit === null ? '' : String(settings.platformMonthlyTokenLimit),
    tenantDefaultRequestLimit:
      settings.tenantDefaultRequestLimit === null ? '' : String(settings.tenantDefaultRequestLimit),
    tenantDefaultTokenLimit:
      settings.tenantDefaultTokenLimit === null ? '' : String(settings.tenantDefaultTokenLimit),
    tenantOwnedProviderAllowed: settings.tenantOwnedProviderAllowed,
    platformFallbackAllowed: settings.platformFallbackAllowed,
  };
}

/** The form state as the PATCH body. */
export function toDeploymentSettingsValues(
  state: DeploymentFormState,
): UpdatePlatformSettingsValues {
  return {
    aiEnabled: state.aiEnabled,
    defaultProviderId: emptyToNull(state.defaultProviderId),
    defaultModelId: emptyToNull(state.defaultModelId.trim()),
    defaultPromptVersion: emptyToNull(state.defaultPromptVersion.trim()),
    maxOutputTokens: parseOptionalNumber(state.maxOutputTokens),
    temperature: parseOptionalNumber(state.temperature),
    platformMonthlyRequestLimit: parseOptionalNumber(state.platformMonthlyRequestLimit),
    platformMonthlyTokenLimit: parseOptionalNumber(state.platformMonthlyTokenLimit),
    tenantDefaultRequestLimit: parseOptionalNumber(state.tenantDefaultRequestLimit),
    tenantDefaultTokenLimit: parseOptionalNumber(state.tenantDefaultTokenLimit),
    tenantOwnedProviderAllowed: state.tenantOwnedProviderAllowed,
    platformFallbackAllowed: state.platformFallbackAllowed,
  };
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** A number, or null when the field is blank. */
function parseOptionalNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
